import React, { useEffect } from 'react';

import SEO from '../SEO';import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { CheckCircle, ArrowRight, ThermometerSun, Droplets, Wind, Snowflake, AlertTriangle, Wrench } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';
import { useNavigate } from 'react-router-dom';

const HVACCooling = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const systemIssues = [
    {
      title: 'Chiller Problems',
      icon: Snowflake,
      issues: ['Tube corrosion and leaks', 'Scale buildup reducing efficiency', 'Erosion at baffles and tube sheets', 'Shell and end cap damage'],
      solutions: 'Tube coating, shell lining, and leak sealing'
    },
    {
      title: 'Cooling Tower Issues',
      icon: Wind,
      issues: ['Basin corrosion and leaks', 'Fill media degradation', 'Structural deterioration', 'Distribution deck damage'],
      solutions: 'Basin coatings, structural repair, leak sealing'
    },
    {
      title: 'Condenser Problems',
      icon: ThermometerSun,
      issues: ['Tube fouling and blockage', 'Waterbox corrosion', 'Tube sheet erosion', 'End cover damage'],
      solutions: 'Protective coatings and surface rebuilding'
    },
    {
      title: 'Piping & Components',
      icon: Droplets,
      issues: ['Pipe corrosion and leaks', 'Valve body damage', 'Pump housing wear', 'Expansion joint failures'],
      solutions: 'Pipe repair wraps, metal rebuilding, leak sealing'
    }
  ];

  const efficiencyImpact = [
    { problem: '1/32" scale buildup', impact: '8-10% efficiency loss' },
    { problem: 'Fouled condenser tubes', impact: '15-20% capacity reduction' },
    { problem: 'Corroded cooling tower', impact: '5-15% efficiency loss' },
    { problem: 'Leaking chiller tubes', impact: 'System shutdown risk' }
  ];

  const serviceCapabilities = [
    'Chiller tube coating and lining',
    'Cooling tower basin restoration',
    'Condenser waterbox protection',
    'Heat exchanger tube plugging',
    'Pipe leak repair without shutdown',
    'Pump and valve rebuilding',
    'Structural steel protection',
    'Secondary containment lining'
  ];

  const benefits = [
    'Restore lost efficiency',
    'Extend equipment life',
    'Reduce energy costs',
    'Prevent unplanned downtime',
    'Improve reliability',
    'Defer capital replacement'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title="HVAC & Cooling System Solutions" description="HVAC and cooling system repair solutions. Chiller, condenser, and cooling tower coating and restoration." path="/research/hvac-cooling" />
      
      <PageHero
        badge="Research & Learn"
        title="HVAC & Cooling Systems"
        description="Cooling systems face unique challenges from corrosion, scale, and biological growth. Learn how to maintain peak efficiency and prevent costly failures."
        backgroundKey="hvac-cooling"
        buttonText="Get HVAC Assessment"
      />

      {/* System Issues */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Common HVAC System Issues</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {systemIssues.map((system, index) => {
              const IconComponent = system.icon;
              return (
                <Card key={index} className="border-t-4 border-t-cyan-600">
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <IconComponent className="text-cyan-600 mr-3" size={28} />
                      {system.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="mb-4">
                      <p className="text-sm font-semibold text-slate-700 mb-2">Common Issues:</p>
                      <ul className="text-sm text-gray-600 space-y-1">
                        {system.issues.map((issue, idx) => (
                          <li key={idx} className="flex items-start">
                            <AlertTriangle className="text-orange-500 mr-2 mt-0.5 flex-shrink-0" size={14} />
                            {issue}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="bg-green-50 p-3 rounded">
                      <p className="text-sm"><span className="font-semibold text-green-700">Our Solutions:</span> {system.solutions}</p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Efficiency Impact */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">The Cost of Neglect</h2>
          <p className="text-gray-600 text-center mb-8 max-w-3xl mx-auto">
            Small problems in HVAC systems compound quickly, leading to significant efficiency losses and increased operating costs.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {efficiencyImpact.map((item, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-sm text-center">
                <p className="text-gray-600 text-sm mb-2">{item.problem}</p>
                <p className="text-2xl font-bold text-red-600">{item.impact}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Capabilities */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Our HVAC Services</h2>
              <p className="text-gray-600 mb-8">
                We provide comprehensive repair and protection solutions for all types of commercial and industrial HVAC equipment, 
                from small chillers to large central plant systems.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {serviceCapabilities.map((capability, index) => (
                  <div key={index} className="flex items-center">
                    <CheckCircle className="text-green-700 mr-2 flex-shrink-0" size={18} />
                    <span className="text-gray-700 text-sm">{capability}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-cyan-700 text-white p-8 rounded-lg">
              <Wrench className="mb-4" size={48} />
              <h3 className="text-2xl font-bold mb-4">Minimize Downtime</h3>
              <p className="text-cyan-100 mb-6">
                Many HVAC repairs can be completed during scheduled maintenance windows or even while systems remain operational, 
                minimizing disruption to your facility.
              </p>
              <Button className="bg-white text-cyan-700 hover:bg-gray-100" onClick={() => navigate('/services/hvac-repair')}>
                View HVAC Repair Services <ArrowRight className="ml-2" size={16} />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-green-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Benefits of Proactive HVAC Maintenance</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-center bg-white p-4 rounded-lg shadow-sm">
                <CheckCircle className="text-green-700 mr-3 flex-shrink-0" size={20} />
                <span className="text-gray-700">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <ThermometerSun className="mx-auto mb-4" size={48} />
          <h2 className="text-3xl font-bold mb-4">Optimize Your Cooling System</h2>
          <p className="text-blue-100 mb-8">
            Let our HVAC specialists assess your system and recommend solutions to improve efficiency and reliability.
          </p>
          <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => navigate('/#contact')}>
            Schedule HVAC Assessment <ArrowRight className="ml-2" size={20} />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HVACCooling;
